import React, { useState, useEffect } from "react";
import '../../styles/main.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { configureEspWebInstallButton } from './configureEspWebInstallButton';
import { API_BASE_URL } from '../../config/api';
import ModelSelect from './ModelSelect';
import {
  PCB_TYPE_OPTIONS,
  MODEL_TYPE_OPTIONS,
  getHardwareTableDefaults,
  getPcbRevisionOptions,
  ADVANCED_ACCESS_CODE,
} from './hardwarePartitionOptions';

// Update interfaces to match database schema
interface BinFile {
  path: string;
  offset: number;
}

interface Release {
  _id: string;
  model: string;
  version: string;
  type: 'dev' | 'beta' | 'prod' | 'depr' | 'factory';
  hardware?: string;
  forceUpdate: boolean;
  clearState: boolean;
  enabled: boolean;
  bins: BinFile[];
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Model {
  _id: string;
  name: string;
  code: string;
  description: string;
}

interface BetaFlasherProps {}

const BetaFlasher: React.FC<BetaFlasherProps> = () => {
    const MANIFEST_BASE_URL = API_BASE_URL || 'https://firmware.fluoraplant.com';
  
  const [releases, setReleases] = useState<Release[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>("null");
  const [selectedHardware, setSelectedHardware] = useState<string>("null");
  const [selectedFirmware, setSelectedFirmware] = useState<string>("null");
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [releasesLoading, setReleasesLoading] = useState<boolean>(false);
  const [videoType, setVideoType] = useState<string>("mini");
  const [models, setModels] = useState<Model[]>([]);
  const [availableHardware, setAvailableHardware] = useState<string[]>([]);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);
  const [advancedUnlocked, setAdvancedUnlocked] = useState<boolean>(false);
  const [advancedCodeInput, setAdvancedCodeInput] = useState<string>('');
  const [advancedCodeError, setAdvancedCodeError] = useState<boolean>(false);
  const [activeHelpTab, setActiveHelpTab] = useState<'instructions' | 'video'>('instructions');
  const [selectedBaudRate, setSelectedBaudRate] = useState<number>(2000000);
  // Partitions to flash (default: firmware/app bin only)
  const [eraseAll, setEraseAll] = useState<boolean>(false);
  const [flashFirmware, setFlashFirmware] = useState<boolean>(true);
  const [flashBootloader, setFlashBootloader] = useState<boolean>(false);
  const [flashPartitionTable, setFlashPartitionTable] = useState<boolean>(false);
  const [flashHardwareTable, setFlashHardwareTable] = useState<boolean>(false);
  // Erase the NVS partition (wipes WiFi credentials). On by default for every flash.
  const [eraseNvs, setEraseNvs] = useState<boolean>(true);
  // Hardware table byte values (byte 2 / factory test is always sent as 1)
  const [hwPcbType, setHwPcbType] = useState<string>('');
  const [hwPcbRevision, setHwPcbRevision] = useState<string>('');
  const [hwModelType, setHwModelType] = useState<string>('');
  
  // Video model options
  const videoModels = [
    { value: "mini", label: "Fluora Mini" },
    { value: "floor", label: "Fluora Floor" }
  ];

  // Baud rate options
  const baudRateOptions = [
    { value: 38400, label: "38400" },
    { value: 57600, label: "57600" },
    { value: 115200, label: "115200" },
    { value: 230400, label: "230400" },
    { value: 460800, label: "460800" },
    { value: 921600, label: "921600" },
    { value: 2000000, label: "2000000 (2 Mbps) (Default)" }
  ];
  
  // Video URLs
  const videoUrls: Record<string, string> = {
    mini: "https://www.youtube.com/embed/xCZVpDnsw08",
    floor: "https://www.youtube.com/embed/ZO-R6dvsofM" 
  };

  // Fetch all models (initial load)
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/models`, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error("Failed to fetch releases:", response.status, errorMessage, "for model:", selectedProduct);
            throw new Error(`Failed to fetch releases: ${errorMessage}`);
          throw new Error("Failed to fetch models");
        }
        const data = await response.json();
        setModels(data.models || []);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchModels();
  }, [API_BASE_URL]);

  // Fetch hardware revisions when a model is selected
  useEffect(() => {
    if (selectedProduct && selectedProduct !== "null") {
      const fetchHardware = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/hardware/type/${encodeURIComponent(selectedProduct)}`, { headers: { 'Accept': 'application/json' } });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error("Failed to fetch releases:", response.status, errorMessage, "for model:", selectedProduct);
            throw new Error(`Failed to fetch releases: ${errorMessage}`);
            throw new Error("Failed to fetch hardware revisions");
          }
          const data = await response.json();
          // Extract revision numbers from hardware objects
          const revisions = (data.hardware || []).map((hw: any) => hw.revision.toString()).sort();
          setAvailableHardware(revisions);
          // Hardware revision is no longer selected in the main form; default it
          // to the first available revision so the Advanced Settings PCB Revision
          // default continues to work.
          setSelectedHardware(revisions.length > 0 ? revisions[0] : "null");
        } catch (error: any) {
          setError(error.message);
          setAvailableHardware([]);
        }
      };
      fetchHardware();
    } else {
      setAvailableHardware([]);
      setSelectedHardware("null");
    }
  }, [selectedProduct, API_BASE_URL]);

  // Fetch releases when a model and hardware are selected
  useEffect(() => {
    if (selectedProduct && selectedProduct !== "null") {
      const fetchReleases = async () => {
        setReleasesLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/releases/${encodeURIComponent(selectedProduct)}`, { headers: { 'Accept': 'application/json' } });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error("Failed to fetch releases:", response.status, errorMessage, "for model:", selectedProduct);
            throw new Error(`Failed to fetch releases: ${errorMessage}`);
          }
          const data = await response.json();
          const usableReleases = (data.releases || []).filter((release: any) =>
            release.enabled && (release.type === "beta" || release.type === "prod")
          );
          setReleases(usableReleases);
        } catch (error: any) {
          setError(error.message);
          setReleases([]);
        } finally {
          setReleasesLoading(false);
        }
      };
      fetchReleases();
    } else {
      setReleases([]);
    }
  }, [selectedProduct, API_BASE_URL]);

  // Default the hardware-table PCB type / model type from the selected model.
  // The PCB revision is intentionally left unselected so it must be chosen.
  useEffect(() => {
    if (selectedProduct && selectedProduct !== "null") {
      const defaults = getHardwareTableDefaults(selectedProduct);
      setHwPcbType(defaults.pcbType);
      setHwModelType(defaults.modelType);
    }
  }, [selectedProduct]);

  // When firmware is being flashed, the PCB type / model type are locked to the
  // values that match the selected model (they must agree with the app binary).
  useEffect(() => {
    if ((eraseAll || flashFirmware) && selectedProduct && selectedProduct !== "null") {
      const defaults = getHardwareTableDefaults(selectedProduct);
      setHwPcbType(defaults.pcbType);
      setHwModelType(defaults.modelType);
    }
  }, [eraseAll, flashFirmware, selectedProduct]);

  // Keep PCB type and model type consistent when the user edits them directly
  // (only possible while firmware is NOT being flashed). Monos PCB <-> Monos
  // model; Fluora PCB <-> a Fluora model.
  const handleHwPcbTypeChange = (value: string) => {
    setHwPcbType(value);
    if (value === '1') {
      setHwModelType('2');
    } else if (hwModelType === '2') {
      setHwModelType('1');
    }
  };

  const handleHwModelTypeChange = (value: string) => {
    setHwModelType(value);
    setHwPcbType(value === '2' ? '1' : '0');
  };

  // The available PCB revisions depend on the PCB type. Clear a stale selection
  // when the PCB type changes to one that no longer offers that revision.
  useEffect(() => {
    if (hwPcbRevision !== '' && !getPcbRevisionOptions(hwPcbType).includes(hwPcbRevision)) {
      setHwPcbRevision('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hwPcbType]);

  // Handle product selection change
  const handleProductChange = (productCode: string) => {
    // Find the model by code to get its name
    const selectedModel = models.find(m => m.code === productCode);
    if (selectedModel) {
      setSelectedProduct(selectedModel.name); // Use the model name for releases
      setSelectedHardware("null"); // Reset hardware selection
      setSelectedFirmware("null"); // Reset firmware selection
      setSelectedRelease(null); // Reset selected release
    }
  };

  // Handle video model selection change
  const handleVideoModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setVideoType(e.target.value);
  };

  // Handle baud rate selection change
  const handleBaudRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBaudRate(parseInt(e.target.value, 10));
  };

  // Validate the advanced settings access code
  const handleAdvancedCodeSubmit = () => {
    if (advancedCodeInput.trim().toLowerCase() === ADVANCED_ACCESS_CODE) {
      setAdvancedUnlocked(true);
      setAdvancedCodeError(false);
    } else {
      setAdvancedCodeError(true);
    }
  };

  // Prefer the beta build for this model. If none is published, use production.
  useEffect(() => {
    const modelReleases = releases.filter(
      (release) => release.model === selectedProduct && release.enabled
    );
    const betaRelease = modelReleases.find((release) => release.type === "beta");
    const prodReleases = modelReleases
      .filter((release) => release.type === "prod")
      .sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' }));
    const nextRelease = betaRelease || prodReleases[0] || null;
    if (nextRelease) {
      setSelectedFirmware(nextRelease.version);
      setSelectedRelease(nextRelease);
    } else {
      setSelectedFirmware("null");
      setSelectedRelease(null);
    }
  }, [releases, selectedProduct]);

  // Ref for the install button container
  const installButtonRef = React.useRef<HTMLDivElement>(null);

  // When the hardware table is being flashed, every field must be chosen before
  // we let the user connect (nothing is prefilled).
  const hardwareTableSelected = eraseAll || flashHardwareTable;
  const hardwareTableComplete =
    !hardwareTableSelected || (hwPcbType !== '' && hwPcbRevision !== '' && hwModelType !== '');
  const canInstall = !!selectedRelease && hardwareTableComplete;

  // Create/update the install button element
  React.useEffect(() => {
    if (!installButtonRef.current) return;
    
    // Clear existing content
    installButtonRef.current.innerHTML = '';
    
    if (!selectedRelease || !canInstall) return;

    // Create the custom element
    const installButton = document.createElement('esp-web-install-button');
    // By default flash only the firmware/app bin. Additional partitions are
    // opted into through the Advanced Settings controls.
    // "Erase All and Reflash All" forces every partition on.
    const includeFirmware = eraseAll || flashFirmware;
    const includeBootloader = eraseAll || flashBootloader;
    const includePartitions = eraseAll || flashPartitionTable;
    const includeHardwareTable = eraseAll || flashHardwareTable;

    const params = new URLSearchParams({
      model: selectedProduct,
      version: selectedRelease.version,
      type: selectedRelease.type === 'prod' ? 'prod' : 'beta',
    });
    params.set('firmware', includeFirmware ? '1' : '0');
    if (includeBootloader) params.set('bootloader', '1');
    if (includePartitions) params.set('partitions', '1');
    if (eraseNvs) params.set('eraseNvs', '1');
    if (includeHardwareTable) {
      params.set('hardwareTable', '1');
      params.set('pcbRevision', hwPcbRevision);
      params.set('pcbType', hwPcbType);
      params.set('modelType', hwModelType);
      params.set('factoryTest', '1');
    }
    if (eraseAll) params.set('eraseAll', '1');
    configureEspWebInstallButton(installButton, {
      manifest: `${MANIFEST_BASE_URL}/manifest/build?${params.toString()}`,
      baudRate: selectedBaudRate,
      trackFirmware: {
        model: selectedProduct,
        toFirmware: selectedRelease.version,
      },
    });
    
    // Create the button element
    const button = document.createElement('button');
    button.setAttribute('slot', 'activate');
    button.className = 'btn btn-primary btn-lg';
    button.textContent = 'Connect To Device';
    
    installButton.appendChild(button);
    installButtonRef.current.appendChild(installButton);
  }, [selectedRelease, selectedProduct, selectedHardware, selectedBaudRate, eraseAll, flashFirmware, flashBootloader, flashPartitionTable, flashHardwareTable, eraseNvs, hwPcbType, hwPcbRevision, hwModelType, canInstall, MANIFEST_BASE_URL]);

  // Install button container
  const InstallButton = canInstall ? (
    <div ref={installButtonRef} />
  ) : (
    <button
      type="button"
      className="btn btn-primary btn-lg"
      disabled
      title={
        !selectedRelease
          ? 'Select a model and firmware version first'
          : 'Set all hardware table fields first'
      }
    >
      Connect To Device
    </button>
  );

  if (error) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  if (initialLoading) {
    return (
      <div className="container-fluid mt-3">
        <div className="row mb-3">
          <div className="col">
            <h3>Beta Firmware</h3>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mx-auto text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading firmware data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="responsive-page-header mb-4">
        <h3 className="mb-1">Beta Firmware Update: USB Cable</h3>
        <p className="text-muted mb-0">Choose your device, beta firmware, and connect over USB to update.</p>
      </div>

      <div className="alert alert-warning mb-2 d-flex align-items-center gap-3">
        <img
          src="/micro-usb-icon.png"
          alt="Micro USB"
          className="flex-shrink-0 d-none d-sm-block"
          style={{ width: '52px', mixBlendMode: 'multiply', opacity: 0.58 }}
        />
        <div>
          <strong>Notice:</strong> This process requires a Micro USB cable. If you don't have a cable, you can purchase a <a href="https://www.amazon.com/Ruaeoda-Micro-Android-Charger-Gold-Plated/dp/B0D7RZJSPS/ref=sr_1_3?crid=2MKC4VLBQYTRC&dib=eyJ2IjoiMSJ9.WR63mKTxAJQICRAe9uZ2ANjc_kDGRQnHyH1azhuB_SMMnQKozBtE0RGQ1g7-4UjfDePK1wQQAPicK3A9_BIrcZsQfixYZYCxc_uZWoITTSF6ismyX_S9sL4TeEBfcWd18uAHOVNChZHGaLQziO2KkLRXFL99JZtcud7lqcQPYCW7q2xxE_rZiecrh-uZ85UWbboz_D2_IlSIpHVmarUR-sgJaixmHWOAK7-XqvfsIMk.S4EmnzigVFUEKfXVTH9phY2RLrre7Ls4u0DMknfU6F0&dib_tag=se&keywords=micro%2Busb%2Bcable&qid=1741565839&sprefix=microusb%2Bcabl%2Caps%2C262&sr=8-3&th=1">Micro USB to USB A cable here</a> or a <a href="https://www.amazon.com/SNANSHI-Charging-Transfer-Headphone-Controller/dp/B0CQX233KN/ref=sr_1_1_sspa?crid=3EWUQ93QGMOZP&dib=eyJ2IjoiMSJ9.z2Qd9EvZDBUDo8Qs3GnxOsTOFVVJcqlKL63xV-H83d1YKgSEdaVMBXM8Aof2YAaYPVl0LVoGuyv2jsIExBwqMKFZQDUAejhRRUF6x4URkazyHMa-d6P7fZyoYjoyH1vwenDRedojY1ebW9OAWNei4RibYQqf-1H-M2I3Arfp4tkE1w4tQK_gaA7zhb2ESpQHNcT6xx4L9rCuRlbruiDHrbYVc9z7smGB8xxVLVu9iQU.K39l_585xSi5ej9EWQibuBwwEhpWt8_mEZ6_OXfbqi8&dib_tag=se&keywords=usb%2Bc%2Bto%2Busb%2Bmicro&qid=1741732298&sprefix=usb%2Bc%2Bto%2Busb%2Bmicro%2Caps%2C220&sr=8-1-spons&sp_csd=d2lkZ2V0TmFtZT1zcF9hdGY&th=1">Micro USB to USB C cable here</a>.
        </div>
      </div>

      <div className="alert alert-warning mb-4 d-flex align-items-center gap-3">
        <span className="flex-shrink-0 d-none d-sm-flex justify-content-center" style={{ width: '52px' }}>
          <FontAwesomeIcon icon={faPlug} style={{ fontSize: '48px', color: '#000', mixBlendMode: 'multiply', opacity: 0.58 }} />
        </span>
        <div>
          <strong>Important:</strong> Your device must be plugged into power for the update to work.
        </div>
      </div>

      <div className="row g-4 align-items-start">
        {/* Main Content Area */}
        <div className="col-lg-5 col-xl-4">
          {/* Flasher Controls */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white">
              <h5 className="mb-0">Update Device</h5>
            </div>
            <div className="card-body p-4">
            {/* Step 1: Product Selection */}
            <div className="mb-4">
              <label htmlFor="product_model" className="form-label fw-bold">Model</label>
              <ModelSelect
                id="product_model"
                className="mb-3"
                placeholder="Choose your model"
                models={models}
                value={selectedProduct === "null" ? "" : models.find(m => m.name === selectedProduct)?.code || ""}
                onChange={handleProductChange}
              />
            </div>

            {/* Step 2: Beta Firmware Version */}
            <div className="mb-4">
              <label htmlFor="firmware_version" className="form-label fw-bold">Beta Firmware Version</label>
              <input
                id="firmware_version"
                type="text"
                className={`form-control mb-3${selectedProduct === "null" || releasesLoading || selectedFirmware === "null" ? ' text-muted' : ''}`}
                readOnly
                value={
                  selectedProduct === "null"
                    ? "Choose a model first"
                    : releasesLoading
                      ? "Loading..."
                      : selectedFirmware !== "null"
                        ? selectedRelease?.type === 'prod'
                          ? `${selectedFirmware} (production)`
                          : `${selectedFirmware} (Beta)`
                        : "No version available"
                }
              />
            </div>

            {/* Step 4: Advanced Settings */}
            <div className="mb-4">
              <button
                type="button"
                className="btn btn-link p-0 text-body fw-bold text-decoration-none d-flex align-items-center gap-2"
                onClick={() => setShowAdvancedSettings((current) => !current)}
                aria-expanded={showAdvancedSettings}
                aria-controls="beta-advanced-settings"
              >
                <span className="fw-bold">Advanced Settings</span>
                <span aria-hidden="true">{showAdvancedSettings ? '▴' : '▾'}</span>
              </button>
              <div
                id="beta-advanced-settings"
                className={`collapse${showAdvancedSettings ? ' show' : ''}`}
              >
                <div className="border rounded bg-light p-3 mt-3">
                  {!advancedUnlocked ? (
                    <div>
                      <p className="text-muted small mb-3">
                        You must inquire with customer service and receive an access code in order to use Advanced Features.
                      </p>
                      <div className="mb-3">
                        <a
                          href="https://wiki.fluoraplant.com/firmware-updates/usb-cable#get-an-access-code-for-advanced-features"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                        >
                          Request Code
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                        </a>
                      </div>
                      <label htmlFor="advanced_code" className="form-label fw-bold d-block">Enter Code</label>
                      <div className="d-flex gap-2">
                        <input
                          id="advanced_code"
                          type="text"
                          className={`form-control${advancedCodeError ? ' is-invalid' : ''}`}
                          placeholder="Enter code"
                          value={advancedCodeInput}
                          onChange={(e) => { setAdvancedCodeInput(e.target.value); setAdvancedCodeError(false); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdvancedCodeSubmit(); } }}
                        />
                        <button type="button" className="btn btn-primary" onClick={handleAdvancedCodeSubmit}>Unlock</button>
                      </div>
                      {advancedCodeError && <div className="text-danger small mt-2">Incorrect code. Please contact customer service.</div>}
                    </div>
                  ) : (
                  <>
                  <label htmlFor="baud_rate" className="form-label fw-bold">Baud Rate</label>
                  <select
                    id="baud_rate"
                    className="form-select mb-3"
                    onChange={handleBaudRateChange}
                    value={selectedBaudRate}
                  >
                    {baudRateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <label className="form-label fw-bold">Erase Storage</label>
                  <div className="form-check mb-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="erase_nvs"
                      checked={eraseNvs}
                      onChange={(e) => setEraseNvs(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="erase_nvs">
                      Erase all NVS storage <em>(you will lose your WiFi credentials)</em>
                    </label>
                  </div>

                  <label className="form-label fw-bold">Select Partitions To Flash</label>
                  <p className="text-muted small mb-2">
                    By default only the firmware is flashed. Only enable the others if you know you need them.
                  </p>
                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input erase-all-switch"
                      type="checkbox"
                      role="switch"
                      id="erase_all"
                      checked={eraseAll}
                      onChange={(e) => setEraseAll(e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="erase_all">Erase and Flash All</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flash_firmware"
                      checked={eraseAll || flashFirmware}
                      disabled={eraseAll}
                      onChange={(e) => setFlashFirmware(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flash_firmware">
                      Firmware{selectedRelease?.version
                        ? ` (v${selectedRelease.version}${selectedRelease.type === 'prod' ? ' (production)' : ''})`
                        : ''}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flash_bootloader"
                      checked={eraseAll || flashBootloader}
                      disabled={eraseAll}
                      onChange={(e) => setFlashBootloader(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flash_bootloader">Bootloader</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flash_partition_table"
                      checked={eraseAll || flashPartitionTable}
                      disabled={eraseAll}
                      onChange={(e) => setFlashPartitionTable(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flash_partition_table">Partition Table</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flash_hardware_table"
                      checked={eraseAll || flashHardwareTable}
                      disabled={eraseAll}
                      onChange={(e) => setFlashHardwareTable(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flash_hardware_table">Hardware Table</label>
                  </div>

                  {(eraseAll || flashHardwareTable) && (
                    <div className="border rounded bg-white p-3 mt-3">
                      <p className="text-muted small mb-3">
                        The hardware table will be written with these values. Factory test performed is always set to <strong>1</strong>.
                      </p>
                      <div className="mb-3">
                        <label htmlFor="hw_pcb_type" className="form-label fw-bold">PCB Type</label>
                        <select
                          id="hw_pcb_type"
                          className={`form-select${hwPcbType === '' ? ' text-muted' : ''}`}
                          value={hwPcbType}
                          disabled={eraseAll || flashFirmware}
                          onChange={(e) => handleHwPcbTypeChange(e.target.value)}
                        >
                          <option value="" disabled>Choose PCB Type...</option>
                          {PCB_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="hw_pcb_revision" className="form-label fw-bold">PCB Revision</label>
                        <select
                          id="hw_pcb_revision"
                          className={`form-select${hwPcbRevision === '' ? ' text-muted' : ''}`}
                          value={hwPcbRevision}
                          onChange={(e) => setHwPcbRevision(e.target.value)}
                        >
                          <option value="" disabled>Choose PCB Revision...</option>
                          {getPcbRevisionOptions(hwPcbType).map((rev) => (
                            <option key={rev} value={rev}>Rev {rev}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-0">
                        <label htmlFor="hw_model_type" className="form-label fw-bold">Model Type</label>
                        <select
                          id="hw_model_type"
                          className={`form-select${hwModelType === '' ? ' text-muted' : ''}`}
                          value={hwModelType}
                          disabled={eraseAll || flashFirmware}
                          onChange={(e) => handleHwModelTypeChange(e.target.value)}
                        >
                          <option value="" disabled>Choose Model Type...</option>
                          {MODEL_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </div>
              </div>
            </div>

            {/* Step 5: Connect to Device Button */}
            <div className="mb-0">
              {InstallButton}
            </div>
            </div>
          </div>
        </div>
                
        {/* Instructions and Video Area */}
        <div className="col-lg-7 col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white">
              <div className="flasher-help-tabs" role="tablist" aria-label="Flasher help">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeHelpTab === 'instructions'}
                  className={`flasher-help-tab${activeHelpTab === 'instructions' ? ' active' : ''}`}
                  onClick={() => setActiveHelpTab('instructions')}
                >
                  Quick Instructions
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeHelpTab === 'video'}
                  className={`flasher-help-tab${activeHelpTab === 'video' ? ' active' : ''}`}
                  onClick={() => setActiveHelpTab('video')}
                >
                  How to Access USB Connector <i>(video)</i>
                </button>
              </div>
            </div>
            <div className="card-body">
              {activeHelpTab === 'instructions' ? (
                <>
                  <div className="flasher-instructions-list">
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">1</span>
                      <span className="flasher-instruction-text">
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline text-start"
                          onClick={() => setActiveHelpTab('video')}
                        >
                          Access the USB connector
                        </button>
                      </span>
                    </div>
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">2</span>
                      <span className="flasher-instruction-text">Plug the USB cable into your computer and device</span>
                    </div>
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">3</span>
                      <span className="flasher-instruction-text">Confirm the device is powered on and the red indicator LED is lit</span>
                    </div>
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">4</span>
                      <span className="flasher-instruction-text">Select the model and confirm the firmware version</span>
                    </div>
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">5</span>
                      <span className="flasher-instruction-text">Click <strong>Connect To Device</strong></span>
                    </div>
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">6</span>
                      <span className="flasher-instruction-text">Choose <strong>USB</strong> when prompted</span>
                    </div>
                    <div className="flasher-instruction-step">
                      <span className="flasher-step-number">7</span>
                      <span className="flasher-instruction-text">Follow the on-screen update instructions</span>
                    </div>
                  </div>
                  <a
                    href="https://wiki.fluoraplant.com/firmware-updates/usb-cable"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flasher-instruction-link"
                  >
                    Detailed instructions →
                  </a>
                </>
              ) : (
                <>
                  <div className="d-flex justify-content-end mb-3">
                    <div className="form-group mb-0" style={{ width: '200px' }}>
                      <select
                        className="form-select form-select-sm"
                        value={videoType}
                        onChange={handleVideoModelChange}
                        aria-label="Select device model for video"
                      >
                        {videoModels.map((model, index) => (
                          <option key={index} value={model.value}>
                            Model: {model.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{
                    width: '100%',
                    position: 'relative',
                    paddingBottom: '56.25%',
                    height: 0,
                    overflow: 'hidden'
                  }}>
                    <iframe
                      src={videoUrls[videoType]}
                      allow="fullscreen; picture-in-picture"
                      allowFullScreen
                      title={`How to Access USB on ${videoType === "mini" ? "Fluora Mini" : "Fluora Floor"}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0
                      }}
                    ></iframe>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetaFlasher;